import { useGetAgendaByIdQuery } from 'store/services/flow/agenda';
import { useGetAgendaViewRecordsQuery } from 'store/services/flow/agendaRecords';
import { useGetUsableViewQuery, useGetUserViewQuery } from 'store/services/flow/views';
import type { AgendaRecordItem, DataSchema, GetAgendaRecordsRes, InlineRecordType, ModalsType, RecordsViewParam, Schema } from 'types';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { RecordValueItem } from './RecordValueItem';
import { Tabs } from 'components/mui/common/Tabs';
import { useTranslation } from 'react-i18next';
import { useValueFormItems, useLocaleCodeIsoNorma } from 'utils/hooks';

import { getValuesByFormItemId } from 'utils/agendaRecord/agendaRecord.ts';
import { formatDisplayValue } from 'utils/agendaRecord/formatDisplayValue';
import { getSelectRowId } from 'utils/table.ts';
import { isObjectArray } from 'utils/typeChecks';
import { isNormalizedValueChanged } from 'utils/common';
import { getUiDefinitionContainerStyles, getUiDefinitionTextStyles } from 'utils/styles';
import { UserAgendaTable } from 'components/user/UserAgendaTable';
import { Box, Typography } from '@mui/material';
import { ActionModal } from 'components/mui/common/ActionModal';
import { RecordsItemWrapper, RecordValueWrapper } from './RecordsItem.styles';
import type { FormItemUiDefinition } from 'types/formItem.ts';
import { SearchableBrowseItem } from 'components/common/SearchableBrowseItem/SearchableBrowseItem';
import { SelectedRowsWrapper, RowChip } from 'components/common/SearchableBrowseItem/styledComponents';
import { useGetValuesDirect } from 'utils/hooks/userAgendaTable';

interface SelectedRow {
  id: string;
  value: string;
  formItemUiDefinition?: FormItemUiDefinition | null;
}

type SelectedRowInput = SelectedRow | string;

const normalizeSelectedRows = (rows: SelectedRowInput[] = []): SelectedRow[] =>
  rows.map((row) => (typeof row === 'string' ? { id: row, value: '' } : row));

interface ModalProps extends ModalsType {
  agendaId: string;
  viewId: string;
  recordId?: string;
  parentAgendaId?: string;
  title: string;
  isMultiSelect: boolean;
  selectedRows?: SelectedRowInput[];
  onSubmit: (e: SelectedRow[]) => void;
  params?: RecordsViewParam[];
  formItemIdToShow: string;
  overrideCalculateValues?: InlineRecordType[];
  initialSearchTerm?: string;
  isAutoSelectSearchResultEnabled?: boolean;
  onAutoSelectConsumed?: () => void;
}

export const RecordModal = memo(
  ({
    open,
    onClose,
    title,
    onSubmit,
    agendaId,
    recordId,
    parentAgendaId,
    viewId,
    isMultiSelect,
    selectedRows: selectedRowsProps = [],
    params,
    formItemIdToShow,
    overrideCalculateValues = [],
    initialSearchTerm = '',
    isAutoSelectSearchResultEnabled = false,
    onAutoSelectConsumed = () => undefined,
  }: ModalProps) => {
    const { t } = useTranslation();
    const locale = useLocaleCodeIsoNorma();
    const [selectedRows, setSelectedRows] = useState<SelectedRow[]>(() => normalizeSelectedRows(selectedRowsProps));

    const { data: { viewName } = {} } = useGetUserViewQuery(
      {
        parentId: agendaId,
        viewId,
        viewParentType: 'Agenda',
      },
      {
        skip: !agendaId || !viewId,
      }
    );

    const { data: agendaData } = useGetAgendaByIdQuery(
      {
        id: agendaId,
      },
      { skip: !agendaId }
    );

    const isHeliosTable = useMemo(() => !!agendaData?.module?.isHeliosVisible, [agendaData]);

    const handleSelectRows = useCallback(
      (rowIds: string[], rows?: Schema<string | number, DataSchema>[]) => {
        const newItems =
          rows?.map((row) => ({
            id: getSelectRowId(row, isHeliosTable),
            value: formatDisplayValue(row[formItemIdToShow], isHeliosTable, locale),
          })) ?? [];

        setSelectedRows((prevState) => {
          const selectedMap = new Map([...prevState, ...newItems].map((item) => [item.id, item]));

          return rowIds.map((id) => selectedMap.get(id)!).filter(Boolean);
        });
      },
      [formItemIdToShow, isHeliosTable, locale]
    );

    const handleAutoSelectSingleResult = useCallback(
      (rowId: string, row: Schema<string | number, DataSchema>) => {
        onAutoSelectConsumed();
        const entry = { id: rowId, value: formatDisplayValue(row[formItemIdToShow], isHeliosTable, locale) };
        const next = isMultiSelect ? [...normalizeSelectedRows(selectedRowsProps), entry] : [entry];
        onSubmit(next);
      },
      [formItemIdToShow, isHeliosTable, isMultiSelect, onAutoSelectConsumed, onSubmit, selectedRowsProps, locale]
    );

    return (
      <ActionModal
        open={open}
        title={title}
        onClose={onClose}
        onCancel={onClose}
        onSubmit={() => onSubmit(selectedRows)}
        maxWidth="lg"
        submitText={t('common.save')}>
        <Tabs
          tabs={[{ id: viewId, label: viewName ?? '' }].map(({ id, label }) => ({
            id,
            label,
            path: '',
            content: (
              <UserAgendaTable
                agendaId={agendaId}
                viewId={viewId}
                recordId={recordId}
                parentAgendaId={parentAgendaId}
                disableRowClick
                isOverview
                isModal
                onSelectRow={handleSelectRows}
                isMultiSelect={isMultiSelect}
                selectedRows={selectedRows.map((row) => row.id)}
                params={params}
                modalOverrideCalculateValues={overrideCalculateValues}
                showAllRows
                initialSearchTerm={initialSearchTerm}
                isAutoSelectSingleResult={isAutoSelectSearchResultEnabled}
                onAutoSelectSingleResult={handleAutoSelectSingleResult}
              />
            ),
          }))}
          selectedTab={viewId}
          onTabClick={() => null}
        />
      </ActionModal>
    );
  }
);

export interface RecordsItemProps {
  moduleId?: string;
  parentAgendaId?: string;
  recordId?: string;
  systemType?: string;
  asColumns?: boolean;
  lightError?: boolean;
  onConfirm?: () => void;
  formItemUiDefinition?: FormItemUiDefinition | null;
  formOptionShouldDirty?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export const RecordsItem = memo(
  ({
    moduleId = '',
    systemType = '',
    asColumns = false,
    onConfirm,
    formItemUiDefinition,
    parentAgendaId,
    recordId: parentRecordId,
    formOptionShouldDirty,
    ...formItem
  }: RecordsItemProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setValue, register, trigger: triggerForm, control } = useFormContext();
    const { viewId, agendaId, formItemIdToShow, params, isMultiSelect = false } = formItem.definition.records;
    const [modalState, setModalState] = useState({
      open: false,
      searchTerm: '',
      isAutoSelectSearchResultEnabled: false,
    });
    const [selectedRows, ...watchedValue] = useWatch({
      control,
      name: [formItem.id, ...(params ?? []).map(({ valueFormItemId }: { valueFormItemId: string }) => valueFormItemId)],
    });
    const isInitialized = useRef(false);
    const oldWatchedValue = useRef(watchedValue);

    useEffect(() => {
      if (!isInitialized.current) return;

      if (watchedValue.some((value) => value === undefined)) {
        return;
      }

      if (watchedValue.map((value, index) => isNormalizedValueChanged(value, oldWatchedValue.current[index])).some(Boolean)) {
        oldWatchedValue.current = watchedValue;
        setValue(formItem.id, [], {
          shouldDirty: formOptionShouldDirty ?? false,
          shouldTouch: false,
        });
      }
    }, [watchedValue, formItem.id, setValue, selectedRows, formOptionShouldDirty]);

    useEffect(() => {
      register(formItem.id, formItem.registerProps);
      triggerForm(formItem.id);
    }, [formItem.id, formItem.registerProps, register, triggerForm]);

    const { data: { viewParameters = [] } = {} } = useGetUsableViewQuery(
      {
        parentId: agendaId,
        viewId: viewId,
        viewParentType: 'Agenda',
      },
      { skip: !viewId }
    );

    const valueFormItems = useValueFormItems({ viewParameters, params });

    const selectedRecordIds = isObjectArray(selectedRows ?? [])
      ? selectedRows.map(({ id }: { id: string }) => id).filter(Boolean)
      : (selectedRows ?? []);

    const directValues = useGetValuesDirect({
      agendaId: parentAgendaId ?? '',
      recordId: parentRecordId ?? '',
      valueFormItems,
      isModal: false,
    });

    const { data: { items = [] } = {} as GetAgendaRecordsRes<AgendaRecordItem[]> } = useGetAgendaViewRecordsQuery(
      {
        params: {
          agendaId,
          viewId,
          page: 1,
          pageSize: 1000,
          search: '',
          orderBy: '',
          formItemId: '',
          orderDesc: false,
        },
        recordIds: selectedRecordIds,
        skipViewConditions: false,
        evaluateVisualAttributes: true,
        valueFormItems: directValues.length ? directValues : valueFormItems,
        filters: [],
      },
      { skip: !selectedRows || selectedRows?.length === 0, refetchOnMountOrArgChange: true }
    );

    const uiDefinitionByRecordId = useMemo(() => {
      const map = new Map<string, FormItemUiDefinition | null>();
      items.forEach((item) => {
        const attr = item.attributes.find((a) => a.formItem?.id === formItemIdToShow);
        map.set(item.id, attr?.visibilityResult?.formItemUiDefinition ?? null);
      });
      return map;
    }, [items, formItemIdToShow]);

    const handleGetValuesByFormItemIdToShow = useCallback(
      (getValue?: boolean) => getValuesByFormItemId(items, formItemIdToShow, getValue),
      [formItemIdToShow, items]
    );

    const getValuesByFormItemIdToShow = useMemo(() => {
      if (!selectedRecordIds.length) {
        return [];
      }

      return handleGetValuesByFormItemIdToShow(true);
    }, [handleGetValuesByFormItemIdToShow, selectedRecordIds.length]);

    useEffect(() => {
      if (!selectedRows?.length) return;

      const needsTransformation = selectedRows.every((row: SelectedRow) => typeof row === 'string');
      if (!needsTransformation) return;

      const selectedIds = selectedRows as string[];
      const filteredItems = (items ?? []).filter((item) => selectedIds.includes(item.id));
      const allFetched = selectedIds.every((id) => filteredItems.some((item) => item.id === id));
      if (!allFetched) return;

      const getValues = getValuesByFormItemId(filteredItems, formItemIdToShow, true);
      if (getValues.length > 0) {
        isInitialized.current = true;
        oldWatchedValue.current = watchedValue;
        setValue(formItem.id, getValues, {
          shouldDirty: formOptionShouldDirty ?? false,
          shouldTouch: false,
        });
      }
    }, [formItem.id, setValue, selectedRows, watchedValue, items, formItemIdToShow, formOptionShouldDirty]);

    const displayValues = useMemo<SelectedRow[]>(() => {
      if (!getValuesByFormItemIdToShow?.length) return [];

      return getValuesByFormItemIdToShow.map((item) => {
        if (typeof item === 'string') {
          return { id: '', value: item };
        }

        const value = Array.isArray(item.value) ? (typeof item.value[0] === 'string' ? item.value[0] : item.value[0]?.text) : item.value;

        return {
          id: item.id,
          value: value || '',
          formItemUiDefinition: uiDefinitionByRecordId.get(item.id) ?? null,
        };
      });
    }, [getValuesByFormItemIdToShow, uiDefinitionByRecordId]);

    const recordId = useMemo(() => {
      const record = getValuesByFormItemIdToShow[0];
      return typeof record === 'object' ? record?.id : '';
    }, [getValuesByFormItemIdToShow]);

    const isEditable = !formItem.detail && !formItem.disabled;

    const toggleModal = useCallback(() => {
      setModalState((prev) => ({
        ...prev,
        open: !prev.open,
        isAutoSelectSearchResultEnabled: false,
      }));
    }, []);

    const handleCloseModal = useCallback(() => {
      setModalState((prev) => ({
        ...prev,
        open: false,
        isAutoSelectSearchResultEnabled: false,
      }));
    }, []);

    const handleOnClick = useCallback(
      (id?: string) => {
        if (!formItem.detail) return toggleModal();

        const url = `/user/modules/${moduleId}/${systemType}/${agendaId}/r/${id ?? recordId}/detail`;
        navigate(url);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [agendaId, formItem.detail, moduleId, recordId, systemType]
    );

    const handleSearchSubmit = useCallback((searchTerm: string, shouldAutoSelect: boolean) => {
      setModalState({
        open: true,
        searchTerm,
        isAutoSelectSearchResultEnabled: shouldAutoSelect,
      });
    }, []);

    const handleClear = useCallback(() => {
      setValue(formItem.id, [], { shouldDirty: true });
      triggerForm(formItem.id);
      onConfirm?.();
    }, [formItem.id, onConfirm, setValue, triggerForm]);

    const handleAutoSelectConsumed = useCallback(() => {
      setModalState((prev) => ({
        ...prev,
        isAutoSelectSearchResultEnabled: false,
      }));
    }, []);

    const renderField = useCallback(() => {
      if (!isMultiSelect) {
        const selectedValue = String(displayValues[0]?.value ?? '');
        const selectedUiDefinition = displayValues[0]?.formItemUiDefinition ?? formItemUiDefinition;

        if (isEditable) {
          return (
            <SearchableBrowseItem
              name={selectedValue}
              onSearchSubmit={handleSearchSubmit}
              isSingleBrowse={true}
              error={Boolean(formItem.error)}
              lightError={Boolean(formItem.lightError)}
              formItemUiDefinition={selectedUiDefinition}
              onClear={handleClear}
            />
          );
        }

        if (!selectedValue) {
          return null;
        }

        return getValuesByFormItemIdToShow.map((item) => (
          <RecordValueItem
            key={typeof item === 'object' ? item.id : item}
            value={
              typeof item === 'string'
                ? item
                : (Array.isArray(item.value) ? (typeof item.value[0] === 'string' ? item.value[0] : item.value[0]?.text) : item.value) || ''
            }
            cursorPointer={true}
            onClick={() => handleOnClick(typeof item === 'object' ? item.id : undefined)}
            formItemUiDefinition={typeof item === 'object' ? (uiDefinitionByRecordId.get(item.id) ?? formItemUiDefinition) : formItemUiDefinition}
          />
        ));
      }

      return isEditable ? (
        <SearchableBrowseItem
          name=""
          onSearchSubmit={handleSearchSubmit}
          isSingleBrowse={false}
          error={Boolean(formItem.error)}
          lightError={Boolean(formItem.lightError)}
        />
      ) : null;
    }, [
      displayValues,
      formItem.error,
      formItem.lightError,
      formItemUiDefinition,
      getValuesByFormItemIdToShow,
      handleClear,
      handleOnClick,
      handleSearchSubmit,
      isEditable,
      isMultiSelect,
      uiDefinitionByRecordId,
    ]);

    return (
      <>
        <RecordsItemWrapper>
          <RecordValueWrapper asColumns={asColumns}>
            {isMultiSelect && displayValues.length > 0 && (
              <SelectedRowsWrapper>
                {displayValues.map((item) => {
                  const containerStyles = getUiDefinitionContainerStyles(item.formItemUiDefinition);
                  const textStyles = getUiDefinitionTextStyles(item.formItemUiDefinition);

                  return (
                    <RowChip
                      key={item.id || item.value}
                      label={<Typography component="span">{item.value}</Typography>}
                      onClick={() => handleOnClick(item.id || undefined)}
                      clickable
                      sx={{
                        ...(containerStyles.backgroundColor && { backgroundColor: containerStyles.backgroundColor }),
                        ...(item.formItemUiDefinition?.columnBorderColor && {
                          border: `1px solid ${item.formItemUiDefinition.columnBorderColor}`,
                        }),
                        ...(textStyles.color && { '& .MuiChip-label .MuiTypography-root': { color: textStyles.color } }),
                        ...(textStyles.fontWeight && { '& .MuiChip-label .MuiTypography-root': { fontWeight: textStyles.fontWeight } }),
                        ...(textStyles.fontStyle && { '& .MuiChip-label .MuiTypography-root': { fontStyle: textStyles.fontStyle } }),
                        ...(textStyles.textDecoration && {
                          '& .MuiChip-label .MuiTypography-root': { textDecoration: textStyles.textDecoration },
                        }),
                      }}
                    />
                  );
                })}
              </SelectedRowsWrapper>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>{renderField()}</Box>
          </RecordValueWrapper>
          {formItem.error && !formItem.lightError && (
            <Typography variant="body1" color="error">
              {formItem.error.message}
            </Typography>
          )}
        </RecordsItemWrapper>
        {modalState.open && (
          <RecordModal
            open={true}
            {...formItem.definition.records}
            title={t('flow.modulesAndAgendas.form.item.records._')}
            formItemIdToShow={formItemIdToShow}
            selectedRows={selectedRows}
            parentAgendaId={parentAgendaId}
            recordId={parentRecordId}
            initialSearchTerm={modalState.searchTerm}
            isAutoSelectSearchResultEnabled={modalState.isAutoSelectSearchResultEnabled}
            onAutoSelectConsumed={handleAutoSelectConsumed}
            onSubmit={(value) => {
              handleCloseModal();
              setValue(formItem.id, value, { shouldDirty: true });
              triggerForm(formItem.id);
              onConfirm?.();
            }}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }
);
